import { SportConfig, SportType } from './sport';
import type { TenantEmbedConfig } from './embed';

// ─── Language types ──────────────────────────────────────────────────────────

/** Supported UI languages for the admin panel and apps */
export type TenantLanguage = 'es' | 'en' | 'ca' | 'va' | 'eu' | 'gl';

export const TENANT_LANGUAGE_LABELS: Record<TenantLanguage, string> = {
  es: 'Español',
  en: 'English',
  ca: 'Català',
  va: 'Valencià',
  eu: 'Euskera',
  gl: 'Galego',
};

// ─── Plan types ─────────────────────────────────────────────────────────────

/** Single plan for federation tenants (pricing is always custom / contact us) */
export type FederationPlan = 'federation';

/** Plans available for club tenants */
export type ClubPlan = 'starter' | 'growth' | 'business';

export type TenantPlan = FederationPlan | ClubPlan;

// ─── Module types ────────────────────────────────────────────────────────────

/**
 * Selectable modules for federation tenants.
 * Each module corresponds to a menu section gated by `has()` in menu.component.ts.
 * Base features (users, clubs, teams, competitions, matches, calendar, referees,
 * emails, notifications, economic management) are always active — they do NOT appear here.
 */
export type FederationModule =
  | 'transfers' // Traspasos de jugadores → menú "Traspasos"
  | 'sanctions' // Sanciones / incidencias → menú "Incidencias"
  | 'change_dates' // Cambios de fecha de partido → menú "Cambios de Fecha"
  | 'advanced_reports' // Informes avanzados → menú "Reportes"
  | 'invoices' // Gestión de facturas → menú "Económico > Facturas"
  | 'receipts' // Gestión de recibos → menú "Económico > Recibos"
  | 'sponsors' // Patrocinadores → menú "Patrocinadores"
  | 'reservations' // Reserva de instalaciones → menú "Reservas"
  | 'technification' // Programas de tecnificación → menú "Tecnificación"
  | 'communications'; // Comunicación (emails + notificaciones + noticias) → menú "Comunicación"

/**
 * Selectable modules for club tenants.
 * Each module corresponds to a menu section gated by `has()` in menu.component.ts.
 * Base features (socios, perfil del club) are always active — they do NOT appear here.
 */
export type ClubModule =
  | 'memberships' // Cuotas con pago online → menú "Cuotas y Pagos"
  | 'competitions' // Competiciones, temporadas, partidos → menú "Competiciones"
  | 'reports' // Exportación de informes PDF/Excel → menú "Informes"
  | 'sponsors' // Patrocinadores → menú "Patrocinadores"
  | 'squad_availability' // Disponibilidad de plantilla → menú "Disponibilidad"
  | 'call_ups' // Convocatorias digitales → menú "Convocatorias"
  | 'reservations' // Reserva de instalaciones → menú "Reservas"
  | 'communications'; // Comunicación (emails + notificaciones + noticias) → menú "Comunicación"

export type TenantModule = FederationModule | ClubModule;

/** Max selectable modules per plan */
export const PLAN_MODULE_SLOTS: Record<TenantPlan, number | 'unlimited'> = {
  // Federation: single plan, always unlimited
  federation: 'unlimited',
  // Club plans
  starter: 2,
  growth: 3,
  business: 'unlimited',
};

/**
 * Modules automatically included (always active) for each plan — do NOT consume a slot.
 * To add a default module to a plan, add it here.
 */
export const PLAN_INCLUDED_MODULES: Record<TenantPlan, TenantModule[]> = {
  federation: [],
  starter: [],
  // Growth incluye cuotas online y competiciones sin consumir slots premium.
  // (La comunicación push/email es una feature base, no un módulo.)
  growth: ['memberships', 'competitions'],
  business: [],
};

/**
 * Modules that are NEVER auto-granted, even on unlimited plans.
 * Must always be explicitly selected by the tenant admin.
 */
export const ALWAYS_OPT_IN_MODULES: TenantModule[] = ['invoices', 'receipts'];

/** All modules available for each tenant type */
export const FEDERATION_MODULES: FederationModule[] = [
  'transfers',
  'sanctions',
  'change_dates',
  'advanced_reports',
  'invoices',
  'receipts',
  'sponsors',
  'reservations',
  'technification',
  'communications',
];

export const CLUB_MODULES: ClubModule[] = [
  'memberships',
  'competitions',
  'reports',
  'sponsors',
  'squad_availability',
  'call_ups',
  'reservations',
  'communications',
];

export const FEDERATION_MODULE_LABELS: Record<FederationModule, string> = {
  transfers: 'Traspasos de jugadores',
  sanctions: 'Sanciones / incidencias',
  change_dates: 'Cambios de fecha',
  advanced_reports: 'Informes avanzados',
  invoices: 'Gestión de facturas',
  receipts: 'Gestión de recibos',
  sponsors: 'Patrocinadores',
  reservations: 'Reserva de instalaciones',
  technification: 'Tecnificación',
  communications: 'Comunicación (emails, notificaciones y noticias)',
};

export const FEDERATION_PLAN_LABELS: Record<FederationPlan, string> = {
  federation: 'Plan Federación',
};

export const CLUB_PLAN_LABELS: Record<ClubPlan, string> = {
  starter: 'Starter',
  growth: 'Growth',
  business: 'Business',
};

export const CLUB_MODULE_LABELS: Record<ClubModule, string> = {
  memberships: 'Cuotas con pago online',
  competitions: 'Competiciones',
  reports: 'Exportación de informes',
  sponsors: 'Patrocinadores',
  squad_availability: 'Disponibilidad de plantilla',
  call_ups: 'Convocatorias digitales',
  reservations: 'Reserva de instalaciones',
  communications: 'Comunicación (emails, notificaciones y noticias)',
};

/**
 * Canonical source of truth: which modules are available for each tenant type.
 *
 * Each entry MUST correspond to a menu section gated in menu.component.ts.
 * To add a new module: add it to the type, FEDERATION/CLUB_MODULES array, labels record,
 * then gate the route (moduleGuard) and menu item (has()) — see admin.instructions.md.
 */
export const MODULES_BY_TYPE: {
  federation: { value: FederationModule; label: string }[];
  club: { value: ClubModule; label: string }[];
} = {
  federation: FEDERATION_MODULES.map((m) => ({
    value: m,
    label: FEDERATION_MODULE_LABELS[m],
  })),
  club: CLUB_MODULES.map((m) => ({ value: m, label: CLUB_MODULE_LABELS[m] })),
};

// ─── Plan catalog ────────────────────────────────────────────────────────────

/**
 * Full definition of a plan — stored in Firestore at `plan_catalog/{id}`.
 * The super-admin edits this. When a plan is assigned to a tenant, the relevant
 * fields (includedModules, premiumSlots) are copied into TenantConfig so the
 * tenant document is self-contained.
 *
 * Document IDs: `federation_federation`,
 *               `club_starter`, `club_growth`, `club_business`
 */
export interface PlanCatalogEntry {
  id: string;
  tenantType: 'federation' | 'club';
  plan: TenantPlan;
  /** Display label shown to tenants (e.g. "Pro", "Growth") */
  label: string;
  /** Short tagline shown in pricing page */
  description: string;
  /** Monthly price in € (0 = custom/contact) */
  priceMonthly: number;
  /** Price per month when billed annually (0 = custom/contact) */
  priceAnnual: number;
  /**
   * Modules always active with this plan at no extra slot cost.
   * For unlimited plans this is ignored — all modules are active.
   */
  includedModules: TenantModule[];
  /**
   * Max number of additional (premium) modules the tenant can activate on top
   * of includedModules. 'unlimited' → no limit.
   */
  premiumSlots: number | 'unlimited';
  /** Whether this plan is selectable for new tenants */
  enabled: boolean;
  /** Display order in the pricing page */
  sortOrder: number;
}

/** Default values to seed the plan catalog in a new environment */
export const DEFAULT_PLAN_CATALOG: Omit<PlanCatalogEntry, 'id'>[] = [
  // ─── Federation (single plan, always contact us) ─────────────────────────
  {
    tenantType: 'federation',
    plan: 'federation',
    label: 'Plan Federación',
    sortOrder: 1,
    description: 'Plataforma completa para federaciones deportivas. Precio personalizado según necesidades.',
    priceMonthly: 0,
    priceAnnual: 0,
    includedModules: [],
    premiumSlots: 'unlimited',
    enabled: true,
  },
  // ─── Club ─────────────────────────────────────────────────────────────────
  {
    tenantType: 'club',
    plan: 'starter',
    label: 'Starter',
    sortOrder: 10,
    description: 'Para clubs que quieren digitalizar su gestión.',
    priceMonthly: 29,
    priceAnnual: 23,
    includedModules: [],
    premiumSlots: 2,
    enabled: true,
  },
  {
    tenantType: 'club',
    plan: 'growth',
    label: 'Growth',
    sortOrder: 11,
    description: 'Más funcionalidades para clubs en crecimiento.',
    priceMonthly: 59,
    priceAnnual: 47,
    includedModules: ['memberships', 'competitions'],
    premiumSlots: 3,
    enabled: true,
  },
  {
    tenantType: 'club',
    plan: 'business',
    label: 'Business',
    sortOrder: 12,
    description: 'Todo incluido para clubs profesionales.',
    priceMonthly: 99,
    priceAnnual: 79,
    includedModules: [],
    premiumSlots: 'unlimited',
    enabled: true,
  },
];

// ─── MembershipBenefit ───────────────────────────────────────────────────────

/**
 * A configurable benefit item shown in the "Ventajas exclusivas" section
 * of the federate/membership page in the mobile app.
 * Stored in `TenantConfig.membershipBenefits[]`.
 */
export interface MembershipBenefit {
  /** Title translations keyed by language code. Use 'es' as the default fallback. */
  title: Partial<Record<TenantLanguage, string>>;
  /** Description translations keyed by language code. */
  description?: Partial<Record<TenantLanguage, string>>;
}

/**
 * How far into the future bookable slots are shown in the app.
 * undefined or 'all' = no upper limit (all future slots).
 */
export type ReservationBookingWindow =
  | 'current_week'
  | 'next_week'
  | 'current_month'
  | 'next_month'
  | 'all';

// ─── RefereePaymentsConfig ────────────────────────────────────────────────────

/**
 * Payment model for referee receipts.
 * - `club_pays`         → current behaviour: the club pays the receipt online
 *                         (payment link/QR) and the federation reimburses later.
 * - `federation_deferred` → clubs do NOT pay. The referee generates and signs
 *                         the receipt and the federation pays at end of season.
 */
export type RefereePaymentsMode = 'club_pays' | 'federation_deferred';

/**
 * Where the referee receipt amounts come from. Orthogonal to the mode.
 * - `referee_input` → the referee types service/displacement amounts (current).
 * - `fixed`         → the configured `fixedServiceFee`/`fixedDisplacementFee`
 *                     are used and the referee cannot edit them.
 */
export type RefereeAmountSource = 'referee_input' | 'fixed';

/**
 * Per-tenant (and per-season overridable) configuration of referee payments.
 * Stored inline in `TenantConfig.refereePaymentsConfig` (pattern of
 * `reservationConfig`) so the app can read it without an extra Firestore call.
 * A season may override it partially (see `BaseSeason.refereePaymentsConfig`);
 * the resolver applies the order Season → Tenant → defaults.
 */
export interface RefereePaymentsConfig {
  /** Payment model. Default `'club_pays'` (retro-compatible). */
  mode: RefereePaymentsMode;
  /** Where receipt amounts come from. Default `'referee_input'`. */
  amountSource: RefereeAmountSource;
  /** IRPF retention rate in % — single source of truth in BOTH modes. */
  retentionRate: number;
  /** Apply retention to the service fee (honorarios). Default `true`. */
  retainService: boolean;
  /** Apply retention to the displacement fee. Default `false`. */
  retainDisplacement: boolean;
  /** Fixed service fee per match, used when `amountSource === 'fixed'`. */
  fixedServiceFee: number;
  /** Fixed displacement fee per match, used when `amountSource === 'fixed'`. */
  fixedDisplacementFee: number;
}

/**
 * Default RefereePaymentsConfig used by the resolver when neither the tenant
 * nor the active season define one (R4). Retro-compatible: current club_pays
 * flow, referee-typed amounts, retention only on service fee.
 */
export const DEFAULT_REFEREE_PAYMENTS_CONFIG: RefereePaymentsConfig = {
  mode: 'club_pays',
  amountSource: 'referee_input',
  retentionRate: 15,
  retainService: true,
  retainDisplacement: false,
  fixedServiceFee: 0,
  fixedDisplacementFee: 0,
};

// ─── Discount ────────────────────────────────────────────────────────────────

export interface TenantDiscount {
  type: 'percentage' | 'fixed';
  /** Percentage (0-100) or fixed euros */
  value: number;
  reason: string;
  expiresAt?: Date;
}

// ─── ClubSportEntry ───────────────────────────────────────────────────────────

/**
 * One sport entry for a multi-sport club tenant.
 * Clubs can manage multiple sports independently — each with its own
 * SportConfig and, optionally, a linked federation.
 *
 * Stored in `TenantConfig.clubSports[]`.
 * The first entry whose `sport` matches `TenantConfig.sport` is the primary sport.
 */
export interface ClubSportEntry {
  /** The sport discipline */
  sport: SportType;
  /** Sport-specific rules, labels, and UI flags for this discipline */
  sportConfig: SportConfig;
  /**
   * Tenant ID of the federation this club is affiliated with for this sport.
   * Undefined when the club is not federated for this sport.
   */
  federationId?: string;
}

// ─── ClubUpsellConfig ────────────────────────────────────────────────────────

/**
 * Cross-sell of the platform's *club* plans inside a **federation** tenant panel.
 *
 * Club managers (role `MANAGER`) working inside a federation tenant only get what
 * the federation gives them (inscriptions, licenses, federation invoices). This
 * config enables an outbound-only CTA (a dismissable card on the club-manager
 * dashboard) that invites them to open their **own** club tenant on the platform
 * landing, where the plans, prices and self-service registration already live.
 *
 * ── Hard constraints (do not "improve" without reading feature #705) ──────────
 *  - Outbound only: the CTA is an absolute link to the platform landing. No lead
 *    is ever written to Firestore — a dedicated-project tenant (e.g. fmpelota-pro)
 *    stores data in the *client's* project, which we would never see.
 *  - Off by default: announcing our product to a federation's own clubs inside
 *    their panel requires the federation's commercial consent, so `clubUpsell`
 *    is absent (⇒ nothing visible) for every existing tenant.
 *  - The resulting club account is INDEPENDENT from the federation panel:
 *    `linkClubToFederation` requires both tenants in the same Firestore, so no
 *    integration between the two accounts may be promised in the UI.
 */
export interface ClubUpsellConfig {
  /** Master switch. Absent or `false` ⇒ nothing is rendered anywhere. */
  enabled: boolean;
  /**
   * REQUIRED absolute URL of the landing self-service registration page, e.g.
   * 'https://www.dms-manager.com/es/registro'. There is no runtime fallback: the
   * card fails closed and is NOT rendered when this is missing, empty, not a
   * valid URL or not http/https — never a broken link. The super-admin tenant
   * form is what guarantees the value (required whenever `enabled` is on, and
   * pre-filled with the platform landing). Point it at
   * 'http://localhost:4202/es/registro' to test locally. The UTM params are
   * appended by the admin panel — do NOT include them here.
   */
  landingUrl: string;
  /**
   * OPTIONAL absolute URL of the landing page that lists what a club plan
   * includes, with the real prices read from `plan_catalog`, e.g.
   * 'https://www.dms-manager.com/es/software-para-clubes-deportivos'. Unlike
   * `landingUrl` this one is optional on purpose: when it is absent, invalid or
   * not http/https the secondary "see everything it includes" link is simply not
   * rendered and the card keeps working with its CTA. No default in code. The
   * UTM params are appended by the admin panel — do NOT include them here.
   */
  infoUrl?: string;
}

// ─── TenantConfig ────────────────────────────────────────────────────────────

/**
 * Firebase client-side configuration.
 * Non-secret — designed to be publicly accessible (security is enforced by Firebase Rules and API key restrictions).
 */
export interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/** Platform-level configuration stored in `tenants/{tenantId}` (root collection). */

/**
 * Estado de la subscripción SaaS de la plataforma (billing hacia nosotros).
 *
 * - `trial`                → Período de prueba activo (14 días desde el registro)
 * - `trial_expired`        → Trial expirado, pendiente de activar subscripción
 * - `pending_subscription` → Tenant creado por superAdmin, sin checkout iniciado
 * - `pending_payment`      → Checkout completado, esperando confirmación SEPA (5-7 días)
 * - `active`               → Subscripción activa y al corriente de pago
 * - `past_due`             → Pago fallido, período de gracia activo (Stripe reintentará)
 * - `cancelled`            → Subscripción cancelada definitivamente
 * - `suspended`            → Suspendido manualmente por superAdmin
 */
export type PlanStatus =
  | 'trial'
  | 'trial_expired'
  | 'pending_subscription'
  | 'pending_payment'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'suspended';

/**
 * Modo de cobro de una licencia adicional (PLAYER / DELEGATE) a un usuario que
 * ya tiene licencia REFEREE. Ver `TenantConfig.refereeExtraLicensePricing`.
 */
export type RefereeExtraLicensePricing = 'supplement' | 'full';

export interface TenantConfig {
  id: string;
  name: string;
  type: 'federation' | 'club';
  /** Deporte principal del tenant */
  sport: SportType;
  sportConfig: SportConfig;
  /**
   * Multi-sport entries for club tenants.
   * When present (and `type === 'club'`), the club manages each sport independently.
   * The entry whose `sport` matches `TenantConfig.sport` is the primary sport.
   * Each entry carries its own `SportConfig` and an optional federation link.
   *
   * For federation tenants and single-sport clubs, leave undefined.
   */
  clubSports?: ClubSportEntry[];
  plan: TenantPlan;
  planStatus: PlanStatus;
  /** Fecha en que expira el trial (solo cuando planStatus = 'trial') */
  trialEndsAt?: Date;
  planExpiresAt?: Date;
  /** Current hours balance (credits - debits). Maintained via Cloud Functions / admin. */
  hoursBalance: number;
  /**
   * Modules explicitly selected by the tenant within their plan allowance.
   * Plans 'federation', 'business' ignore this — all modules are active.
   */
  modules: TenantModule[];
  /**
   * Modules explicitly disabled for this tenant.
   * Only meaningful for unlimited plans ('federation', 'business') where all
   * modules are active by default. Allows opt-out of specific modules.
   */
  disabledModules?: TenantModule[];
  /**
   * Modules always active for this tenant by virtue of their plan definition.
   * Populated automatically from the PlanCatalogEntry when the plan is assigned.
   * These do NOT count against the premiumSlots quota.
   */
  includedModules?: TenantModule[];
  /** Optional discount applied by super-admin */
  discount?: TenantDiscount;
  /** Stripe customer ID for billing — set lazily on first checkout */
  stripeCustomerId?: string;
  /** Stripe subscription ID for the SaaS platform subscription */
  stripeSubscriptionId?: string;
  /**
   * Stripe Product ID for this tenant — created once, reused across price changes.
   * Each price change creates a new Stripe Price under the same Product.
   */
  stripeProductId?: string;
  /**
   * Stripe Price ID for this tenant's SaaS subscription.
   * Created automatically by superAdminSetTenantPrice when the price is configured.
   * Each tenant has their own Price object in Stripe to allow custom amounts.
   */
  stripePriceId?: string;
  /** Holded contact ID — set on first invoice creation */
  holdedContactId?: string;
  /**
   * Negotiated SaaS price for this tenant (in €).
   * Applies to the billing interval defined in `billingInterval`.
   */
  planPriceMonthly?: number;
  /** Billing interval for the SaaS subscription. */
  billingInterval?: 'month' | 'year';
  paymentConfig: {
    gateway: 'stripe' | 'redsys' | 'none';
    /** Whether the online gateway (Stripe/Redsys) is currently active. */
    gatewayEnabled?: boolean;
    /** Whether bank transfer payment is currently active. */
    bankTransferEnabled?: boolean;
    /** IBAN shown to members when paying by bank transfer. */
    iban?: string;
  };
  branding: {
    logoUrl: string;
    /** Hex primary brand color (e.g. '#2563EB') */
    primaryColor?: string;
    /** PNG ≥1024×1024 — app icon source. Stored at tenants/{id}/branding/icon */
    iconUrl?: string;
    /** PNG ≥2732×2732 — splash screen source. Stored at tenants/{id}/branding/splash */
    splashUrl?: string;
    /** Hex background color for icon generation (e.g. '#ffffff') */
    iconBackgroundColor?: string;
    /** Hex background color for splash screen generation (e.g. '#ffffff') */
    splashBackgroundColor?: string;
    /**
     * Full-width cover image shown in the Hero slider of the federation Home screen.
     * When absent, the app uses the local fallback `assets/images/home-cover-fallback.png`.
     */
    coverImageUrl?: string;
  };
  /** UI languages supported by this tenant (admin panel and apps) */
  languages: TenantLanguage[];
  /**
   * Firebase project ID for this tenant.
   * Standard tenants share the master project.
   * Enterprise tenants have their own dedicated Firebase project.
   */
  firebaseProjectId: string;
  /**
   * Full Firebase client-side config for enterprise tenants that have their own Firebase project.
   * When present, the admin panel connects to this project instead of the shared master.
   * The Firebase client config is NOT secret — it is designed to be publicly accessible.
   * Standard tenants omit this field and use the shared master project.
   */
  firebaseClientConfig?: FirebaseClientConfig;
  /** IDs de las federaciones a las que pertenece este club (solo type === 'club').
   * Para clubs multi-deporte, es la unión de todos los `federationId` de `clubSports`.
   * Mantenido automáticamente — no editar directamente. */
  federationIds?: string[];
  /**
   * Custom hostnames pointing to this tenant's admin panel.
   * E.g. ['admin.ffpcv.es', 'gestion.mifederacion.com']
   *
   * Each hostname must also have an entry in the `hostname_index/{hostname}`
   * collection (managed by the setTenantCustomDomain Cloud Function).
   * Firebase Hosting custom domains are added separately via Firebase Console.
   */
  customDomains?: string[];
  adminEmail?: string;
  adminUid?: string;
  onboardingStatus?: 'pending' | 'invited' | 'active';
  onboardingInvitedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  /**
   * Country calling code used for phone/WhatsApp links in the app (e.g. '+34').
   * Default: '+34'.
   */
  phoneCountryCode?: string;
  /**
   * Benefits shown in the "Ventajas exclusivas" section of the federate /
   * membership page in the mobile app. Configurable per tenant from the admin panel.
   * When empty or undefined, the section is hidden.
   */
  membershipBenefits?: MembershipBenefit[];
  /**
   * Configuration for the facility reservation module.
   * Stored inline in TenantConfig so the app can read it without extra Firestore calls.
   */
  reservationConfig?: {
    /**
     * Minimum hours before the slot start time within which a reservation can be cancelled.
     * 0 or undefined = cancellations are always allowed.
     * Example: 24 → reservations can only be cancelled if the slot starts more than 24 h from now.
     */
    cancellationHoursLimit?: number;
    /**
     * Minimum hours of advance notice required to reserve a slot.
     * 0 or undefined = a slot can be booked right up until its start time
     * (only already-started slots are hidden).
     * Example: 2 → a slot can only be booked if it starts more than 2 h from now.
     */
    minHoursBeforeBooking?: number;
    /**
     * How far into the future bookable slots are shown in the app.
     * undefined or 'all' = no upper limit (all future slots).
     */
    bookingWindow?: 'current_week' | 'next_week' | 'current_month' | 'next_month' | 'all';
  };
  /**
   * Cross-sell of the platform's club plans shown to club managers (MANAGER)
   * inside a federation tenant panel. Absent or `enabled: false` ⇒ nothing is
   * rendered (default for every existing tenant). See `ClubUpsellConfig`.
   */
  clubUpsell?: ClubUpsellConfig;
  /**
   * Referee payments configuration (mode, amount source, IRPF retention and
   * fixed amounts). Stored inline (pattern of `reservationConfig`) so the app
   * can read it without an extra Firestore call. When absent, the resolver
   * falls back to `DEFAULT_REFEREE_PAYMENTS_CONFIG` (current club_pays flow).
   */
  refereePaymentsConfig?: RefereePaymentsConfig;
  /**
   * Cómo se cobra la licencia PLAYER / DELEGATE a un usuario que ya tiene
   * licencia REFEREE en la temporada (lo lee `calcLicenseAmount` en functions).
   *
   * - `'supplement'` (o ausente, valor por defecto): regla de la FFPCV. Si el
   *   árbitro ya tiene otra licencia (PLAYER/DELEGATE/INDEPENDENT) no se cobra
   *   nada; si no, se cobra el suplemento `supplements[for=PLAYER]` de la
   *   tarifa REFEREE general (0 € cuando no hay suplemento configurado).
   * - `'full'`: la licencia REFEREE se ignora y la nueva licencia se cobra como
   *   a cualquier otro usuario (tarifa íntegra, descontando solo otras
   *   licencias PLAYER/DELEGATE/INDEPENDENT que ya tenga).
   *
   * Solo lo edita el super admin desde la ficha de tenant de plataforma.
   */
  refereeExtraLicensePricing?: RefereeExtraLicensePricing;
  /**
   * Contact and social media links for the tenant. Configurable from the admin
   * Settings panel and stored inline in TenantConfig so the app can read them
   * without extra Firestore calls. All fields are optional; empty/undefined
   * channels are not rendered in the mobile app.
   */
  social?: {
    /** Public website URL (absolute, e.g. 'https://example.org'). */
    web?: string;
    /** Contact email address (opens with mailto: in the app). */
    email?: string;
    /** Contact phone number (opens with tel: in the app). */
    phone?: string;
    /** Instagram profile URL. */
    instagram?: string;
    /** Facebook page URL. */
    facebook?: string;
    /** Twitter / X profile URL. */
    twitter?: string;
    /** TikTok profile URL. */
    tiktok?: string;
    /** YouTube channel URL. */
    youtube?: string;
    /** WhatsApp phone number (opens with https://wa.me/ in the app). */
    whatsapp?: string;
  };
  /**
   * Dominios en los que se puede incrustar el widget de competiciones
   * (feature 513). Solo lo escribe la callable `saveEmbedAllowedDomains`; las
   * reglas no dejan que un cliente lo toque. Ausente ⇒ la lista se deriva de
   * `customDomains` y `social.web`; presente ⇒ manda, aunque esté vacía.
   */
  embed?: TenantEmbedConfig;
}

/** Fecha a partir de la cual Veri*factu es obligatorio (RD 1007/2023) */
export const VERIFACTU_MANDATORY_DATE = new Date('2027-01-01T00:00:00');

// ─── HostnameIndex ──────────────────────────────────────────────────────────

/**
 * Firestore document stored at `hostname_index/{hostname}` (URL-safe encoded).
 *
 * Maps a custom hostname to the tenant that owns it.
 * Used by the admin panel at startup (pre-auth) to load tenant branding
 * when a user visits the admin via a custom domain (e.g. admin.ffpcv.es).
 *
 * Managed exclusively via the `setTenantCustomDomain` Cloud Function.
 */
export interface HostnameIndex {
  /** Tenant that owns this hostname */
  tenantId: string;
  /** ISO date when this mapping was created */
  createdAt: string;
}

/**
 * Estado de adhesión del tenant a Veri*factu.
 *
 * - `pending`   → No ha completado la configuración.
 * - `enabled`   → Activado voluntariamente antes de la fecha obligatoria.
 * - `mandatory` → Activo porque se alcanzó la fecha obligatoria (01-01-2027).
 * - `exempt`    → Exento (por ley o por resolución administrativa).
 */
export type VerifactuTenantStatus = 'pending' | 'enabled' | 'mandatory' | 'exempt';

/**
 * Stored at `tenants/{tenantId}/settings/verifactu`.
 *
 * Configuración Veri*factu por tenant.
 * Los datos del emisor (NIF, nombre, dirección...) son específicos de este
 * documento y pueden diferir de los de `billing_info` cuando la entidad
 * emisora ante la AEAT difiere de la entidad de facturación.
 *
 * La activación puede ser:
 * - Voluntaria (el tenant la activa antes del 01-01-2027).
 * - Automática (la plataforma la activa al alcanzar la fecha obligatoria).
 * - Exenta    (el tenant aporta la base legal de exención).
 */
export interface VerifactuTenantSettings {
  id: 'verifactu'; // Document ID fijo

  // ── Estado ────────────────────────────────────────────────────────────────
  status: VerifactuTenantStatus;

  /**
   * `true` cuando el tenant ha activado Veri*factu voluntariamente
   * O cuando la fecha obligatoria ha llegado y no está exento.
   * La Cloud Function que genera facturas comprueba este flag.
   */
  enabled: boolean;

  /**
   * Si es `true`, este tenant está exento de la obligación.
   * Requiere `exemptionBasis` para ser válido.
   */
  exempt: boolean;

  /**
   * Base legal de la exención (ej. "Régimen simplificado", "Módulos",
   * "Artículo 7.1 Ley 37/1992 — No sujeción al IVA").
   * Obligatorio cuando `exempt === true`.
   */
  exemptionBasis?: string;

  // ── Datos del emisor ante la AEAT ─────────────────────────────────────────
  /**
   * NIF/CIF del emisor tal como debe figurar en los registros Veri*factu.
   * Normalmente coincide con `billing_info.nif`.
   */
  issuerNif: string;

  /** Razón social del emisor (tal como aparece en el Censo de la AEAT) */
  issuerName: string;

  /** Dirección fiscal completa (calle y número) */
  issuerAddress: string;

  /** Código postal */
  issuerPostalCode: string;

  /** Municipio */
  issuerCity: string;

  /** País (ISO 3166-1 alpha-2). Default 'ES' */
  issuerCountry: string;

  // ── Series de facturación ─────────────────────────────────────────────────
  /**
   * Prefijo de la serie de facturas Veri*factu.
   * Ejemplo: "FAC" → genera "FAC-2025-0001", "FAC-2025-0002"…
   * Debe ser único dentro del NIF del emisor.
   */
  defaultSeries: string;

  // ── Envío a AEAT ──────────────────────────────────────────────────────────
  /**
   * Si `true`, la plataforma envía automáticamente las facturas a la AEAT
   * tras el encadenamiento (estado PENDING_SUBMIT → SUBMITTED).
   * Si `false`, el envío es manual desde el panel de contabilidad.
   */
  autoSubmit: boolean;

  // ── Aceptación de Términos y Condiciones ────────────────────────────────
  /**
   * Fecha en que el admin aceptó los T&C que autorizan a la plataforma
   * a actuar como Tercero Presentador ante la AEAT.
   */
  termsAcceptedAt?: Date;

  /** UID del admin que aceptó los T&C */
  termsAcceptedBy?: string;

  // ── Trazabilidad ──────────────────────────────────────────────────────────
  /** Fecha en que el tenant activó (o fue activado para) Veri*factu */
  activatedAt?: Date;

  /** UID del admin que realizó la última modificación */
  updatedBy?: string;

  updatedAt?: Date;
}

/**
 * Stored at `tenants/{tenantId}/settings/billing_info`.
 * Used to populate invoices and receipts with the tenant's legal data.
 */
export interface TenantBillingInfo {
  id: string; // always 'billing_info'
  legalName: string; // Razón social
  nif: string; // NIF / CIF
  address: string; // Dirección (calle y número)
  city: string; // Ciudad
  postalCode: string; // Código postal
  province: string; // Provincia
  /** ISO 3166-1 alpha-2 country code (e.g. 'ES', 'FR'). Default: 'ES' */
  country?: string;
  /** EU VAT number for reverse charge (e.g. 'ES12345678A'). When present and valid, IVA = 0% */
  vatNumber?: string;
  phone?: string; // Teléfono (opcional)
  email?: string; // Email de facturación (opcional)
  logoUrl?: string; // Download URL del logo para PDFs (opcional)
  logoPath?: string; // Storage path del logo (para regenerar URL si hace falta)
  signerName?: string; // Nombre del firmante para certificados (p.ej. "María García López")
  signerTitle?: string; // Cargo del firmante (p.ej. "Presidenta")
  signatureImageUrl?: string; // Download URL de la imagen de firma/sello para certificados
  signatureImagePath?: string; // Storage path de la imagen de firma (para regenerar/borrar)
  /**
   * Nota legal de exención de IVA que se imprime al pie de las facturas
   * (p.ej. "Exento de IVA según artículo 20.1.13 de la Ley 37/1992").
   * Solo se muestra en el PDF si tiene valor. Opcional.
   */
  vatExemptionNote?: string;
}

/**
 * Stored at `tenants/{tenantId}/settings/season`.
 * Editable by tenant admins (not super-admin-only).
 */
export interface TenantSeasonSettings {
  id: string; // always 'season'
  /**
   * 0-indexed month at which the sports season changes (0 = January, 6 = July).
   * Default: 6 (season changes in July).
   */
  seasonChangeMonth: number;
}

// ─── Notification recipients ──────────────────────────────────────────────────

/**
 * Types of institutional notification whose recipient mailboxes are configurable
 * per tenant. Each type maps to one or more email addresses stored in
 * `tenants/{tenantId}/settings/notification_recipients`.
 *
 * `general` is the fallback bucket: when a specific type has no configured
 * recipients, the platform falls back to `general` (and, in turn, to the
 * tenant's `social.email` / `adminEmail`).
 */
export type TenantNotificationType =
  | 'transferRequests'
  | 'matchDateChanges'
  | 'refereeAssignments'
  | 'refereePayments'
  | 'unsubscribeRequests'
  | 'general';

/** Canonical ordered list of configurable notification types. */
export const TENANT_NOTIFICATION_TYPES: TenantNotificationType[] = [
  'transferRequests',
  'matchDateChanges',
  'refereeAssignments',
  'refereePayments',
  'unsubscribeRequests',
  'general',
];

/**
 * i18n keys (not literal labels) for each notification type. The admin UI
 * resolves these via Transloco under `settings.notifications.types.*`.
 */
export const TENANT_NOTIFICATION_TYPE_LABELS: Record<
  TenantNotificationType,
  string
> = {
  transferRequests: 'settings.notifications.types.transferRequests',
  matchDateChanges: 'settings.notifications.types.matchDateChanges',
  refereeAssignments: 'settings.notifications.types.refereeAssignments',
  refereePayments: 'settings.notifications.types.refereePayments',
  unsubscribeRequests: 'settings.notifications.types.unsubscribeRequests',
  general: 'settings.notifications.types.general',
};

/**
 * Stored at `tenants/{tenantId}/settings/notification_recipients`.
 *
 * Holds the institutional recipient mailboxes per notification type. A missing
 * key or an empty array for a type means "no specific recipients" → the sender
 * applies the fallback chain (general → social.email / adminEmail).
 *
 * This document is member-readable / admin-writable (never public), so the
 * institutional mailboxes are not exposed to anonymous users.
 */
export interface TenantNotificationRecipients {
  id: 'notification_recipients';
  /** List of emails per type. Absent key or empty array ⇒ use fallback. */
  recipients: Partial<Record<TenantNotificationType, string[]>>;
  updatedAt?: Date;
  updatedBy?: string;
}

/**
 * Firebase Auth custom claims shape — multi-tenant.
 *
 * A single Firebase Auth user can belong to multiple tenants simultaneously
 * (e.g. player federated in two federations, manager of two clubs).
 *
 * ── Role model ───────────────────────────────────────────────────────────────
 * Each user has one **primary role** per tenant (stored in `tenants` map).
 * This primary role is used by Firestore security rules and for menu gating.
 *
 * Additionally, users can hold **secondary roles** in the same tenant
 * (stored in `additionalRoles`). These are used client-side for feature
 * gating and do NOT affect Firestore rules.
 *
 * Common multi-role scenarios:
 *  - PLAYER + DELEGATE      (club member who also delegates their team)
 *  - PLAYER + REFEREE       (plays and officiates in a smaller federation)
 *  - ADMIN + PLAYER         (club owner who also competes)
 *  - REFEREE + REFEREE_COORDINATOR (senior referee who coordinates others)
 *
 * ── Active tenant ────────────────────────────────────────────────────────────
 * Mobile apps are built for a specific tenant (`environment.tenantId`).
 * `TenantContextService` uses `APP_TENANT_ID` to select the correct active
 * tenant rather than relying on map insertion order.
 *
 * ── Membership status ────────────────────────────────────────────────────────
 * Membership status (active / pending / suspended) is stored in the Firestore
 * user document (`tenants/{tenantId}/users/{uid}.membershipStatus`), NOT in
 * JWT claims, to allow real-time updates without token refresh.
 */
export interface TenantAuthClaims {
  /**
   * Map of tenantId → primary UserRole.
   * Used by Firestore security rules (string comparison).
   * Example: { "fed-valenciana": "PLAYER", "club-xyz": "MANAGER" }
   */
  tenants: Record<string, string>;
  /**
   * Secondary roles per tenant — client-side feature gating only.
   * Not used in Firestore security rules.
   * Example: { "fed-valenciana": ["DELEGATE", "REFEREE"] }
   */
  additionalRoles?: Record<string, string[]>;
  /** Type of the primary/active tenant — for UI routing. */
  tenantType?: 'federation' | 'club';
  /** Only for clubs federated under a federation. */
  federationId?: string;
  superAdmin?: true;
}

// ─── Onboarding ───────────────────────────────────────────────────────────────

/**
 * Contact info filled by the tenant admin during onboarding wizard.
 * Stored at `tenants/{tenantId}/settings/contact_info`.
 */
export interface TenantContactInfo {
  id: 'contact_info';
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  position?: string; // Cargo en la organización
}

/**
 * Invitation document for tenant onboarding.
 * Stored at `tenant_invitations/{token}` (platform collection).
 * Token is also the document ID — used directly in the wizard URL.
 *
 * Lifecycle: pending → completed | expired
 */
export interface TenantInvitation {
  /** Document ID === token (secure random UUID) */
  id: string;
  tenantId: string;
  /** Email to which the invitation was sent */
  adminEmail: string;
  status: 'pending' | 'completed' | 'expired';
  createdAt: Date;
  expiresAt: Date;
  createdByUid: string;
}
